import React, { useState, useEffect, useCallback } from 'react';

import {
  Autocomplete as AutoMaterial,
  TextField,
  AutocompleteProps as AutoMaterialProps,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
  Typography
} from '@mui/material';

type AutocompleteProps<T> = Omit<
  AutoMaterialProps<T, undefined, undefined, undefined>,
  'value' | 'renderInput' | 'onChange'
> & {
  label: string;
  hiddenLabel?: boolean;
  /**
   * value: object or value
   * if object is passed, equality will be checked using the optionLookupKey on both objects, options[i][optionLookupKey] === value[optionLookupKey]
   * if value if passed like number or string: options[i][optionLookupKey] === value
   */
  value: Partial<T> | string | number;
  /**
   * optionLabelKey: property of object used to show text in the dropdown.
   */
  optionLabelKey?: keyof T;
  /**
   * optionLabelKeys: properties of object used to show text in the dropdown.
   */
  optionLabelKeys?: (keyof T)[];
  /**
   * optionLookupKey: property of object used to check equality with value.
   */
  optionLookupKey?: keyof T;

  additionalOptions?: Partial<T>[];

  onChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: NonNullable<T>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>
  ) => void;
};

export const Autocomplete = <T extends any>({
  label,
  hiddenLabel,
  optionLabelKey,
  optionLookupKey = 'id' as keyof T,
  value,
  onChange,
  optionLabelKeys,
  additionalOptions,
  options,
  ...props
}: AutocompleteProps<T>) => {
  const [internalValue, setInternalValue] = useState<T>(null);

  // useEffect(() => {
  //   const generated: T[] =
  //     additionalOptions?.map(o => {
  //       const obj: Partial<T> = {};
  //       const keys = optionLabelKeys?.length
  //         ? optionLabelKeys
  //         : [optionLabelKey];
  //       obj[keys[0]] = o.label as any;
  //       obj[optionLookupKey] = o.value;
  //       return obj as T;
  //     }) || [];
  //   setGeneratedOptions([...generated, ...options]);
  // }, [options]);

  // const [generatedOptions, setGeneratedOptions] = useState<T[]>([]);

  useEffect(() => {
    setInternalValue(
      generatedOptions?.find(
        o =>
          o === value ||
          o[optionLookupKey] === ((value as T)?.[optionLookupKey] || value)
      )
    );
  }, [value]);

  const generatedOptions: T[] = [
    ...((additionalOptions as T[]) || []),
    ...(options || [])
  ];

  useEffect(() => {
    setInternalValue(
      generatedOptions?.find(
        o =>
          o === value ||
          o[optionLookupKey] === ((value as T)?.[optionLookupKey] || value)
      )
    );
  }, [value, options, additionalOptions]);

  const getOptionLabel = useCallback(
    (o: T) => {
      return props?.getOptionLabel
        ? props?.getOptionLabel(o)
        : o[optionLabelKey]?.toString() ||
            optionLabelKeys?.map(k => o[k]).join(' ');
    },
    [props.getOptionLabel]
  );

  return (
    <AutoMaterial<T>
      value={(internalValue as any) || null}
      onChange={onChange || (() => void 0)}
      isOptionEqualToValue={(o, v) =>
        o === v || o[optionLookupKey] === (v?.[optionLookupKey] || v)
      }
      getOptionLabel={getOptionLabel}
      renderOption={(props, o) => (
        <li {...props}>
          <Typography variant="body4">{getOptionLabel(o)}</Typography>
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={label}
          label={hiddenLabel ? null : label}
        />
      )}
      options={generatedOptions}
      {...props}
    />
  );
};
